import React from 'react';

import PropTypes from 'prop-types';
import cn from 'classnames';
import { useHistory, Link, Route } from 'react-router-dom';
import { BsHouseDoorFill } from 'react-icons/bs';

import { Menu, BreadcrumbItem } from 'modules/common';

import * as c from './constants';

export const BreadedContext = React.createContext();

const MobileView = ({ paths, isMobile }) => {
  const history = useHistory();
  const ref = React.useRef();
  const [isOpen, setIsOpen] = React.useState(false);

  const handleOnClick = () => setIsOpen(!isOpen);
  const handleOnChange = (to) => {
    history.push(to);
  };

  if (paths?.length > 2) {
    const { 0: first, length: l, [l - 1]: last } = paths;
    const middle_paths = paths.slice(1, -1);

    return (
      <>
        <Link className="text-sm font-semibold capitalize" to={first?.url}>
          <BreadcrumbItem label={first?.label} />
        </Link>

        {middle_paths?.length > 0 && (
          <>
            <div className="mx-1.5">{c.mobileSeparator}</div>
            <div className="relative" ref={ref}>
              <button
                className={cn(
                  'text-sm font-black tracking-wide cursor-pointer',
                  isMobile ? 'block' : 'block lg:hidden'
                )}
                onClick={handleOnClick}
                type="button"
              >
                ...
              </button>

              <div className="-ml-ds-24">
                <Menu
                  options={middle_paths}
                  isOpen={isOpen}
                  width="200px"
                  onChange={(item) => handleOnChange(item?.url)}
                  clickOutsideProps={{
                    ref,
                    set: setIsOpen,
                  }}
                />
              </div>
            </div>

            <div className="mx-1.5">{c.mobileSeparator}</div>
          </>
        )}

        <Link className="text-sm font-semibold" to={last?.url}>
          <BreadcrumbItem label={last?.label} />
        </Link>
      </>
    );
  }

  return paths.map((x, i) => (
    <React.Fragment key={`${x?.url}-${x?.label}`}>
      {i > 0 && <div className="mx-1.5">{c.mobileSeparator}</div>}
      <Link className="text-sm font-semibold capitalize" to={x.url}>
        <BreadcrumbItem label={x?.label} />
      </Link>
    </React.Fragment>
  ));
};

MobileView.defaultProps = {
  paths: [],
  isMobile: false,
};

MobileView.propTypes = {
  paths: PropTypes.instanceOf(Array),
  isMobile: PropTypes.bool,
};

const DesktopView = ({ paths }) => (
  <div className="hidden lg:flex items-center gap-ds-16">
    {paths.map((x, i) => (
      <React.Fragment key={`${x?.url}-${x?.label}`}>
        {i > 0 && <div className="-ml-ds-8">{c.desktopSeparator}</div>}

        <Link to={x.url} className="self-stretch flex items-center">
          <BreadcrumbItem label={x.label} />
        </Link>
      </React.Fragment>
    ))}
  </div>
);

DesktopView.defaultProps = {
  paths: [],
};

DesktopView.propTypes = {
  paths: PropTypes.instanceOf(Array),
};

export const Breadcrumb = ({ isMobile, withHome }) => {
  const { path } = React.useContext(BreadedContext);
  const paths = React.useMemo(() => {
    const list = Object.keys(path)
      .sort((a, b) => a.length - b.length)
      .map((key) => ({
        url: key,
        label: path[key],
      }));
    return list;
  }, [path]);

  return (
    <div className="flex items-center gap-2">
      {withHome && (
        <Link to="/">
          <BsHouseDoorFill className="h-4 w-4 text-brand-primary-base" />
        </Link>
      )}

      {isMobile ? (
        <>
          {withHome && <div className="mx-1.5">{c.mobileSeparator}</div>}
          <MobileView paths={paths} isMobile={isMobile} />
        </>
      ) : (
        <>
          <div className="hidden lg:flex items-center gap-1">
            {withHome && <div className="mr-3">{c.desktopSeparator}</div>}
            <DesktopView paths={paths} />
          </div>

          <div className="flex lg:hidden gap-1 items-center py-ds-8">
            {withHome && <div className="mx-1.5">{c.mobileSeparator}</div>}
            <MobileView paths={paths} />
          </div>
        </>
      )}
    </div>
  );
};

Breadcrumb.defaultProps = {
  isMobile: false,
  withHome: false,
};

Breadcrumb.propTypes = {
  isMobile: PropTypes.bool,
  withHome: PropTypes.bool,
};

export const BreadedRoute = ({ label, url, children, ...props }) => {
  const { setPath } = React.useContext(BreadedContext) || {};

  React.useEffect(() => {
    if (!setPath) return () => {};

    setPath((state) => {
      const newState = { ...state };
      newState[url] = label;
      return newState;
    });

    return () => {
      setPath((state) => {
        const oldState = { ...state };
        delete oldState[url];
        return oldState;
      });
    };
  }, [label, url, setPath]);

  return <Route {...props}>{children}</Route>;
};

BreadedRoute.defaultProps = {
  children: null,
};

BreadedRoute.propTypes = {
  label: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Object),
  ]),
};

function Breaded({ children }) {
  const [path, setPath] = React.useState({});

  return (
    <BreadedContext.Provider value={{ path, setPath }}>
      {children}
    </BreadedContext.Provider>
  );
}
Breaded.defaultProps = {};

Breaded.propTypes = {
  children: PropTypes.instanceOf(Object).isRequired,
};

export default Breaded;
